// src/pages/ActiveSessionsPage.js
import React, { useEffect, useState, useRef } from 'react';
import { auth, db } from '../firebaseConfig';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import {
  currentDeviceId,
  signOutDevice,
  signOutAllDevices,
  signOutOtherDevices,
  getMaxSessions,
  SESSIONS_ENABLED,                 // ← use the flag
} from '../sessionManager';
import Layout from '../components/Layout';

export default function ActiveSessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayLimit, setDisplayLimit] = useState(null);

  // loading flags
  const [pendingById, setPendingById] = useState({});
  const [pendingOthers, setPendingOthers] = useState(false);
  const [pendingAll, setPendingAll] = useState(false);

  // tiny toast
  const [toast, setToast] = useState({ kind: '', text: '' });
  const toastTimer = useRef(null);

  const myId = currentDeviceId();

  useEffect(() => {
    // If sessions are disabled, do not attach any Firestore listeners.
    if (!SESSIONS_ENABLED) {
      setLoading(false);
      setSessions([]);
      setDisplayLimit(null);
      return;
    }

    let unsub = () => {};
    async function init() {
      const user = auth.currentUser;
      if (!user) return;

      // real limit -> display (n - 1)
      const n = await getMaxSessions();
      setDisplayLimit(Math.max(1, (Number(n) || 4) - 1));

      const col = collection(db, 'users', user.uid, 'sessions');
      unsub = onSnapshot(query(col, orderBy('lastSeenAt', 'desc')), (snap) => {
        setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      });
    }
    init();
    return () => {
      try { unsub(); } catch {}
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  function showToast(kind, text, ms = 1800) {
    setToast({ kind, text });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast({ kind: '', text: '' }), ms);
  }

  // When disabled, guard all actions (extra safety).
  if (!SESSIONS_ENABLED) {
    return (
      <Layout>
        <div style={{ maxWidth: 800, margin: '24px auto', padding: '0 16px' }}>
          <h2>Active Sessions</h2>
          <div
            style={{
              marginTop: 12,
              padding: '12px 14px',
              borderRadius: 10,
              background: '#fffbeb',
              border: '1px solid #fde68a',
              color: '#92400e'
            }}
          >
            Session management is temporarily disabled while we improve login reliability.
            You’re signed in on this device. If you need to revoke other devices, please
            contact support and we’ll do it for you.
          </div>
        </div>
      </Layout>
    );
  }

  async function handleSignOutThis(id) {
    const user = auth.currentUser;
    if (!user) return;
    setPendingById(p => ({ ...p, [id]: true }));
    try {
      await signOutDevice(user, id);
      showToast('ok', id === myId ? 'Signed out of this device' : 'Device signed out');
    } catch (e) {
      console.error(e);
      showToast('err', 'Could not sign out this session');
    } finally {
      setPendingById(p => {
        const copy = { ...p };
        delete copy[id];
        return copy;
      });
    }
  }

  async function handleSignOutOthers() {
    const user = auth.currentUser;
    if (!user) return;
    setPendingOthers(true);
    try {
      await signOutOtherDevices(user);
      showToast('ok', 'Signed out of other devices');
    } catch (e) {
      console.error(e);
      showToast('err', 'Could not sign out other devices');
    } finally {
      setPendingOthers(false);
    }
  }

  async function handleSignOutAll() {
    const user = auth.currentUser;
    if (!user) return;
    setPendingAll(true);
    try {
      await signOutAllDevices(user);
      showToast('ok', 'Signed out of all devices');
    } catch (e) {
      console.error(e);
      showToast('err', 'Could not sign out all devices');
    } finally {
      setPendingAll(false);
    }
  }

  return (
    <Layout>
      <div style={{ maxWidth: 800, margin: '24px auto', padding: '0 16px' }}>
        <h2>Active Sessions</h2>
        <p style={{ marginTop: 8 }}>
          Maximum allowed devices/browsers:&nbsp;
          <strong>{displayLimit ?? '…'}</strong>
        </p>

        {toast.text ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 8,
              padding: '8px 12px',
              borderRadius: 8,
              background: toast.kind === 'ok' ? '#ecfdf5' : '#fef2f2',
              color: toast.kind === 'ok' ? '#065f46' : '#991b1b',
              border: `1px solid ${toast.kind === 'ok' ? '#a7f3d0' : '#fecaca'}`
            }}
          >
            {toast.text}
          </div>
        ) : null}

        {loading ? (
          <p style={{ marginTop: 16 }}>Loading…</p>
        ) : sessions.length === 0 ? (
          <p style={{ marginTop: 16 }}>No active sessions.</p>
        ) : (
          <div style={{ marginTop: 16 }}>
            {sessions.map(s => {
              const isThis = s.id === myId;
              const pending = !!pendingById[s.id];

              return (
                <div
                  key={s.id}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 10,
                    padding: 12,
                    marginBottom: 10,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    opacity: pending ? 0.7 : 1
                  }}
                >
                  <div style={{ maxWidth: '70%' }}>
                    <div style={{ fontWeight: 600 }}>
                      {isThis ? 'This device' : 'Other device'}
                      {s.revoked ? ' • Revoked' : ''}
                    </div>
                    <div style={{ fontSize: 14, color: '#555', wordBreak: 'break-word' }}>
                      {s.userAgent || 'Unknown agent'}
                    </div>
                    <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>
                      Last seen: {s.lastSeenAt?.toDate?.().toLocaleString?.() || '—'}
                    </div>
                  </div>

                  <div>
                    <button
                      onClick={() => handleSignOutThis(s.id)}
                      style={{
                        padding: '8px 12px',
                        cursor: pending ? 'not-allowed' : 'pointer',
                        background: pending ? '#e5e7eb' : '#fff'
                      }}
                      disabled={pending || pendingOthers || pendingAll}
                      aria-busy={pending ? 'true' : 'false'}
                      title={
                        isThis
                          ? 'Sign out this device remotely (will sign you out here)'
                          : 'Sign out this device'
                      }
                    >
                      {pending ? 'Signing out…' : isThis ? 'Sign out this device' : 'Sign out'}
                    </button>
                  </div>
                </div>
              );
            })}

            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button
                onClick={handleSignOutOthers}
                style={{
                  padding: '10px 14px',
                  cursor: pendingOthers ? 'not-allowed' : 'pointer',
                  background: pendingOthers ? '#e5e7eb' : '#fff'
                }}
                disabled={pendingOthers || pendingAll}
                aria-busy={pendingOthers ? 'true' : 'false'}
              >
                {pendingOthers ? 'Signing out…' : 'Sign out other devices'}
              </button>
              <button
                onClick={handleSignOutAll}
                style={{
                  padding: '10px 14px',
                  cursor: pendingAll ? 'not-allowed' : 'pointer',
                  background: pendingAll ? '#e5e7eb' : '#fff'
                }}
                disabled={pendingAll || pendingOthers}
                aria-busy={pendingAll ? 'true' : 'false'}
              >
                {pendingAll ? 'Signing out…' : 'Sign out all devices'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
